// Pure, framework-free turn-detection heuristics for speech mode.
//
// The speech-mode controller uses these to decide what to do each time the
// transcription service reports a finished segment (a ~VAD silence boundary):
//
//   'empty'      → nothing meaningful said yet
//   'filler'     → only thinking-out-loud noise ("okay", "um", "sigh", "alright so")
//   'incomplete' → real content, but it trails off mid-thought → ask the LLM gate
//   'complete'   → a self-contained utterance → submit the turn
//
// Keeping this here (under app/javascript so the ~ alias resolves) lets it be
// unit-tested with `node --test` independently of the DOM/Stimulus controller.
// See doc/plans/2026-06-20-voice-smart-turn-detection.md

// Acknowledgement / hesitation noise that should never, on its own, end a turn.
// Deliberately excludes real one-word answers ("yes", "no", "sure", "nope").
export const FILLER_WORDS = new Set([
  'um',
  'umm',
  'uh',
  'uhh',
  'uhm',
  'erm',
  'er',
  'ah',
  'oh',
  'hmm',
  'hmmm',
  'hm',
  'mm',
  'mmm',
  'mhm',
  'mmhmm',
  'huh',
  'eh',
  'okay',
  'ok',
  'kay',
  'alright',
  'right',
  'so',
  'well',
  'like',
  'anyway',
  'sigh',
  'cough',
  'dah',
  'blah',
  'la',
])

// When an utterance ENDS on one of these, the speaker is almost certainly still
// going (dangling conjunction / preposition / article / auxiliary / pronoun).
export const DANGLING_WORDS = new Set([
  // conjunctions
  'and',
  'or',
  'but',
  'so',
  'because',
  'cause',
  'cuz',
  'nor',
  'yet',
  'plus',
  // subordinators / interrogatives that lead into more
  'if',
  'when',
  'while',
  'since',
  'although',
  'though',
  'unless',
  'until',
  'whereas',
  'that',
  'which',
  'who',
  'whom',
  'whose',
  'where',
  'why',
  'how',
  'what',
  // prepositions
  'to',
  'for',
  'of',
  'with',
  'about',
  'into',
  'onto',
  'from',
  'by',
  'at',
  'in',
  'on',
  'as',
  'than',
  'then',
  'over',
  'under',
  'between',
  'through',
  'without',
  // articles / determiners / possessives
  'a',
  'an',
  'the',
  'my',
  'your',
  'our',
  'their',
  'his',
  'her',
  'its',
  'this',
  'these',
  'those',
  'some',
  'any',
  // pronouns / auxiliaries that normally precede more words
  'i',
  'we',
  'you',
  'they',
  'he',
  'she',
  'it',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'am',
  'will',
  'would',
  'can',
  'could',
  'should',
  'shall',
  'may',
  'might',
  'must',
  'do',
  'does',
  'did',
  'have',
  'has',
  'had',
  'gonna',
  'wanna',
  'going',
])

const WORD_RE = /[a-z0-9']+/gi

// Lowercased word tokens, punctuation stripped. "Okay, so... what's up?" → ['okay','so','what's','up']
export function tokenize(text) {
  const matches = String(text ?? '')
    .toLowerCase()
    .match(WORD_RE)
  return matches || []
}

export function isFiller(word) {
  return FILLER_WORDS.has(word)
}

// True only when there ARE words and every one of them is filler.
export function isOnlyFiller(text) {
  const tokens = tokenize(text)
  return tokens.length > 0 && tokens.every(isFiller)
}

export function endsWithDanglingWord(text) {
  const tokens = tokenize(text)
  return tokens.length > 0 && DANGLING_WORDS.has(tokens[tokens.length - 1])
}

export function classifyTurn(text) {
  const tokens = tokenize(text)
  if (tokens.length === 0) return 'empty'
  if (tokens.every(isFiller)) return 'filler'
  if (endsWithDanglingWord(text)) return 'incomplete'
  return 'complete'
}
